import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { apiFetch } from "@/utils/api";

interface RequestOptions {
  requireCustomer: boolean;
  requireNeededByDate: boolean;
  requireSerialNotes: boolean;
}

export function AdminRequestOptionsPage() {
  const [options, setOptions] = useState<RequestOptions>({
    requireCustomer: false,
    requireNeededByDate: false,
    requireSerialNotes: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch<{ options: RequestOptions }>("/admin/request-options")
      .then((d) => setOptions(d.options))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const data = await apiFetch<{ options: RequestOptions }>("/admin/request-options", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options),
      });
      setOptions(data.options);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Shipping Options"
        subtitle="Configure defaults and requirements for shipping."
      />
      <div className="bg-white rounded-lg shadow p-6 space-y-6 max-w-lg">
        <div className="flex items-center justify-between">
          <Label htmlFor="require-customer">Require customer</Label>
          <Switch
            id="require-customer"
            checked={options.requireCustomer}
            onCheckedChange={(checked) => setOptions({ ...options, requireCustomer: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="require-needed-by">Require needed-by date</Label>
          <Switch
            id="require-needed-by"
            checked={options.requireNeededByDate}
            onCheckedChange={(checked) => setOptions({ ...options, requireNeededByDate: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="require-request-serial">Require serial notes on line items</Label>
          <Switch
            id="require-request-serial"
            checked={options.requireSerialNotes}
            onCheckedChange={(checked) => setOptions({ ...options, requireSerialNotes: checked })}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button
          onClick={() => void save()}
          disabled={saving}
          className="bg-[#1e3a4f] hover:bg-[#163041]"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        {saved && <p className="text-sm text-green-700">Settings saved.</p>}
      </div>
    </div>
  );
}
